import React from 'react';
import { useTranslation } from 'react-i18next';
import Avatar from '../design/Avatar';
import Icon, { IconName } from '../design/Icon';
import LanguageToggle from './LanguageToggle';
import { useSchoolAuth } from '../auth/SchoolAuthContext';

export interface NavItem {
  key: string;
  label: string;
  icon: IconName;
  active: boolean;
  onClick: () => void;
}

interface AppLayoutProps {
  userName: string;
  userSubtitle: string;
  navItems?: NavItem[];
  children: React.ReactNode;
}

/** Navy side bar (top bar on mobile) + header with user, language and sign-out. */
const AppLayout: React.FC<AppLayoutProps> = ({
  userName,
  userSubtitle,
  navItems = [],
  children,
}) => {
  const { t } = useTranslation();
  const { signOut } = useSchoolAuth();

  const handleSignOut = async (): Promise<void> => {
    try {
      await signOut();
    } catch (err) {
      console.error('[school] signOut failed', err);
    }
  };

  // The app's global CSS locks html/body/#root to the viewport (overflow: hidden),
  // so the layout fills #root and scrolls inside <main> instead of the page.
  return (
    <div className="flex min-h-0 flex-1 flex-col bg-school-canvas text-school-ink lg:flex-row">
      <aside className="flex shrink-0 items-center justify-between gap-3 bg-school-navy px-4 py-2.5 text-white lg:w-60 lg:flex-col lg:items-stretch lg:justify-start lg:overflow-y-auto lg:px-4 lg:py-6">
        <div className="flex min-w-0 items-center gap-2 lg:mb-6 lg:justify-center">
          <img src="/logo.png" alt="" className="h-9 w-9 rounded-lg lg:h-12 lg:w-12" />
          <div className="min-w-0 leading-tight">
            <div className="truncate text-lg font-extrabold">{t('school_app_name')}</div>
            <div className="hidden truncate text-xs text-white/60 sm:block">
              {t('school_app_tagline')}
            </div>
          </div>
        </div>
        {navItems.length > 0 && (
          <nav className="flex gap-1 overflow-x-auto lg:flex-col">
            {navItems.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={item.onClick}
                className={`flex min-h-[44px] items-center gap-3 whitespace-nowrap rounded-xl px-3 text-sm font-medium transition-colors ${
                  item.active
                    ? 'bg-school-navy-soft text-white'
                    : 'text-white/70 hover:bg-white/5 hover:text-white'
                }`}
              >
                <Icon name={item.icon} className="h-5 w-5" />
                {item.label}
              </button>
            ))}
          </nav>
        )}
      </aside>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="flex shrink-0 items-center justify-between gap-3 border-b border-school-line bg-school-surface px-4 py-3 sm:px-8">
          <div className="flex min-w-0 items-center gap-3">
            <Avatar name={userName} />
            <div className="min-w-0 leading-tight">
              <div className="truncate font-semibold">{userName}</div>
              <div className="truncate text-xs text-school-muted">{userSubtitle}</div>
            </div>
          </div>
          <div className="flex items-center gap-1 text-school-muted">
            <LanguageToggle />
            <button
              type="button"
              onClick={handleSignOut}
              className="inline-flex min-h-[40px] items-center gap-1.5 rounded-xl px-3 text-sm font-medium hover:bg-black/5"
            >
              <Icon name="logout" className="h-4 w-4" flipRtl />
              <span className="hidden sm:inline">{t('school_sign_out')}</span>
            </button>
          </div>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <main className="mx-auto w-full max-w-6xl px-4 py-6 sm:px-8 sm:py-8">{children}</main>
        </div>
      </div>
    </div>
  );
};

export default AppLayout;
